import { APP_INITIALIZER, Injectable, Provider } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import { Observable, of } from 'rxjs';
import { filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ClearNotifications } from '../notification';
import { CloseWindow, SetSimpleRouterState } from './simple-router.actions';
import { SimpleRouterState } from './simple-router.state';

@UntilDestroy()
@Injectable()
export class NavigationWatcherService {
  constructor(private actions$: Actions, private router: Router, private store: Store) {}

  init(): void {
    this.clearNotificationsOnBackwardNavigation();
    this.setSimpleRouterStateOnNavigationAndRegisterAutoClose();
  }

  private clearNotificationsOnBackwardNavigation(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart && event.navigationTrigger === 'popstate'),
        tap(() => this.store.dispatch(new ClearNotifications())),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private setSimpleRouterStateOnNavigationAndRegisterAutoClose(): void {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        switchMap(() => this.store.dispatch(new SetSimpleRouterState(this.router.routerState.snapshot))),
        switchMap(() => this.registerForAutoClosePages()),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private registerForAutoClosePages(): Observable<unknown> {
    return this.store.selectSnapshot(SimpleRouterState.isAutoClosePage)
      ? this.router.events.pipe(
          filter((e): e is NavigationStart => e instanceof NavigationStart),
          take(1),
          takeUntil(this.actions$.pipe(ofActionDispatched(CloseWindow))),
          switchMap(e => (e.navigationTrigger === 'popstate' ? this.store.dispatch(new CloseWindow()) : of(null)))
        )
      : of(null);
  }
}

export const NavigationWatcherServiceInitializer: Provider = {
  provide: APP_INITIALIZER,
  useFactory: (navigationWatcherService: NavigationWatcherService) => () => navigationWatcherService.init(),
  deps: [NavigationWatcherService],
  multi: true
};
